import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import {
  setIsPrimaryAnimatedSideBarOpen,
  setPrimarySideBarDataTag,
} from '../../ducks/calendarActions';
import { SIDEBAR_DRAWER_BREAKPOINT } from '../../constants/sidebarDimensions';

/** Small screens only - matches the breakpoint the sidebar becomes a drawer at. */
const ToggleButton = styled('button')`
  display: none;
  background: ${(props) => (props.isOpen ? 'rgba(68, 68, 68, 0.1)' : 'none')};
  border: none;
  padding: 0;
  margin-right: 4px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: ${(props) => (props.isOpen ? '#303133' : '#444444')};
  transition: background-color 200ms ease-in-out, color 200ms ease-in-out;

  @media screen and (max-width: ${SIDEBAR_DRAWER_BREAKPOINT}px) {
    display: flex;
  }

  &:active {
    background: rgba(68, 68, 68, 0.16);
  }

  &:focus-visible {
    outline: 2px solid #1787FB;
    outline-offset: 1px;
  }
`;

/**
 * The chevron is drawn pointing right and rotated 180deg to point left, rather
 * than swapping path data - the closed and open shapes are the same polyline
 * reversed, so one rotation about its own centre gives an animatable flip.
 * `transform-box: fill-box` is what makes `transform-origin: center` resolve to
 * the path's own bounding box instead of the SVG user-space origin.
 */
const Chevron = styled('path')`
  transform-box: fill-box;
  transform-origin: center;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'none')};
  transition: transform 250ms ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Hamburger that shows/hides the calendar sidebar drawer on small screens.
 *
 * The rail plus panel are hidden by default on phones so the calendar gets the
 * full viewport width. Opening from here lands on the calendar picker tab -
 * that's the tab that's actually useful on a phone now that exercises are added
 * through MobileAddExercisePicker rather than dragged out of the sidebar.
 *
 * The bottom bar is shortened to make room for a chevron that points the way the
 * panel moves: right when closed, left when open. The chevron sits low and right
 * of the bar stack rather than inside it - at ~6px rendered it stays legible,
 * which a contained version at half this size did not.
 *
 * It is dropped 1.2 units below a naive centre on the bottom bar so that its top
 * cap clears the middle bar by ~2 units. Centred, the cap came within 0.95 units
 * of that bar while the bars themselves sit 4 apart, and the crowding read as a
 * mistake.
 *
 * The bars deliberately do not morph into an X: that would read as "dismiss" and
 * discard the direction cue the chevron exists to give. The button's active
 * state reinforces it.
 */
const MobileSideBarToggle = () => {
  const dispatch = useDispatch();
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const programType = useSelector((state) => state.calendar.data.programType);

  /** free-form programs have no date-based calendar picker, so start on notes */
  const defaultDataTag = programType === 'free-form-program' ? 'note' : 'calendar2';

  const handleToggle = () => {
    if (isPrimaryAnimatedSideBarOpen) {
      /** IconSideBar's effect clears both data tags once this flips false */
      dispatch(setIsPrimaryAnimatedSideBarOpen(false));
      return;
    }

    dispatch(setIsPrimaryAnimatedSideBarOpen(true));
    /** wait out the panel's width transition before mounting content, same as IconSideBar */
    setTimeout(() => {
      dispatch(setPrimarySideBarDataTag(defaultDataTag));
    }, 250);
  };

  return (
    <ToggleButton
      type='button'
      onClick={handleToggle}
      isOpen={isPrimaryAnimatedSideBarOpen}
      aria-expanded={isPrimaryAnimatedSideBarOpen}
      aria-label={isPrimaryAnimatedSideBarOpen ? 'Close sidebar' : 'Open sidebar'}
    >
      <svg
        width='24'
        height='24'
        viewBox='0 0 24 24'
        aria-hidden='true'
        focusable='false'
      >
        <path
          d='M3 5h13M3 11h13M3 17h7'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          fill='none'
        />
        <Chevron
          isOpen={isPrimaryAnimatedSideBarOpen}
          d='M13.6 15.1 L16.9 18.4 L13.6 21.7'
          stroke='currentColor'
          strokeWidth='1.9'
          strokeLinecap='round'
          strokeLinejoin='round'
          fill='none'
        />
      </svg>
    </ToggleButton>
  );
};

export default MobileSideBarToggle;
